import React, { useEffect, useState } from 'react';
import { ArrowRight, Clock3, HandPlatter, ShoppingBag, Store, Truck } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { APP_BASE } from '@/lib/api';
import { createDefaultAboutPageContent } from '@/data/aboutPageDefaults';

const normalizeAboutContent = (content) => ({
  ...createDefaultAboutPageContent(),
  ...(content || {}),
});

export const AboutPage = () => {
  const [content, setContent] = useState(() => createDefaultAboutPageContent());

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, []);

  useEffect(() => {
    let mounted = true;

    const fetchAboutContent = async () => {
      try {
        const response = await fetch(`${APP_BASE}/api/public/about-us.php`, {
          cache: 'no-store',
        });

        if (!response.ok) return;

        const data = await response.json();
        if (!mounted) return;

        setContent(normalizeAboutContent(data?.content));
      } catch (error) {
        console.error('Error fetching about page content:', error);
      }
    };

    fetchAboutContent();

    return () => {
      mounted = false;
    };
  }, []);

  const highlights = [
    {
      icon: HandPlatter,
      title: content.value_1_title,
      text: content.value_1_text,
    },
    {
      icon: ShoppingBag,
      title: content.value_2_title,
      text: content.value_2_text,
    },
    {
      icon: Clock3,
      title: content.value_3_title,
      text: content.value_3_text,
    },
  ];

  const serviceCards = [
    {
      icon: Store,
      title: 'Ritiro in sede',
      text: "Scegli il ritiro e passa da noi all'orario piu comodo.",
    },
    {
      icon: Truck,
      title: 'Consegna a domicilio',
      text: 'Ordina online e ricevi tutto in modo rapido e organizzato.',
    },
  ];

  return (
    <section className="min-h-screen bg-[linear-gradient(180deg,#fcfaf8_0%,#f6efe8_48%,#fffaf6_100%)] px-4 pb-14 pt-28">
      <div className="mx-auto max-w-6xl space-y-8">
        <div className="overflow-hidden rounded-[36px] border border-[#eaded5] bg-white/85 shadow-[0_20px_70px_rgba(126,101,82,0.12)] backdrop-blur">
          <div className="grid gap-8 px-6 py-8 sm:px-8 lg:grid-cols-[1.05fr_0.95fr] lg:px-10 lg:py-10">
            <div className="space-y-5">
              <div className="inline-flex items-center rounded-full border border-[#dcccc0] bg-[#fff6ef] px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-[#8c7364]">
                {content.badge}
              </div>

              <div className="space-y-4">
                <h1 className="max-w-3xl text-4xl font-bold leading-tight text-[#584941] sm:text-5xl">
                  {content.title}
                </h1>
                <p className="max-w-2xl text-base leading-7 text-[#77665d] sm:text-lg">
                  {content.subtitle}
                </p>
              </div>

              <div className="flex flex-col gap-3 sm:flex-row">
                <Button
                  asChild
                  className="h-12 rounded-2xl bg-[#8f7f74] px-6 text-white hover:bg-[#796a61]"
                >
                  <Link to="/">
                    Vai al menu
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>

                <Button
                  asChild
                  variant="outline"
                  className="h-12 rounded-2xl border-[#d8ccc3] bg-white px-6 text-[#5b4d46] hover:bg-[#faf4ef]"
                >
                  <Link to="/menu-completo">Scopri il menu completo</Link>
                </Button>
              </div>
            </div>

            <div className="grid gap-4">
              {serviceCards.map((card) => {
                const Icon = card.icon;

                return (
                  <div
                    key={card.title}
                    className="rounded-[28px] border border-[#eaded5] bg-[linear-gradient(135deg,#fffaf5_0%,#f7efe8_100%)] p-5 shadow-sm"
                  >
                    <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-[#f1e2d5] text-[#8f7f74]">
                      <Icon className="h-6 w-6" />
                    </div>
                    <h2 className="text-xl font-bold text-[#564740]">{card.title}</h2>
                    <p className="mt-2 text-sm leading-6 text-[#7c6a60]">{card.text}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
          <div className="rounded-[32px] border border-[#eaded5] bg-white p-6 shadow-[0_18px_55px_rgba(126,101,82,0.08)] sm:p-8">
            <p className="text-base leading-8 text-[#67574f]">{content.intro}</p>

            <div className="mt-8 space-y-8">
              <div>
                <h2 className="text-2xl font-bold text-[#584941]">{content.story_title}</h2>
                <p className="mt-3 text-base leading-7 text-[#74645b]">{content.story_text}</p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-[#584941]">{content.quality_title}</h2>
                <p className="mt-3 text-base leading-7 text-[#74645b]">{content.quality_text}</p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-[#584941]">{content.service_title}</h2>
                <p className="mt-3 text-base leading-7 text-[#74645b]">{content.service_text}</p>
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div className="rounded-[32px] border border-[#eaded5] bg-[#8f7f74] p-6 text-white shadow-[0_18px_55px_rgba(126,101,82,0.15)] sm:p-8">
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-white/70">
                {content.values_title}
              </p>

              <div className="mt-5 space-y-4">
                {highlights.map((item) => {
                  const Icon = item.icon;

                  return (
                    <div
                      key={item.title}
                      className="rounded-[24px] border border-white/15 bg-white/10 p-4 backdrop-blur"
                    >
                      <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-2xl bg-white/14">
                        <Icon className="h-5 w-5" />
                      </div>
                      <h3 className="text-lg font-bold">{item.title}</h3>
                      <p className="mt-2 text-sm leading-6 text-white/82">{item.text}</p>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="rounded-[32px] border border-[#eaded5] bg-[linear-gradient(135deg,#fff8f1_0%,#f5ede6_100%)] p-6 shadow-sm sm:p-8">
              <h2 className="text-2xl font-bold text-[#584941]">{content.cta_title}</h2>
              <p className="mt-3 text-base leading-7 text-[#74645b]">{content.cta_text}</p>

              <div className="mt-5 flex flex-col gap-3">
                <Button
                  asChild
                  className="h-12 rounded-2xl bg-[#8f7f74] text-white hover:bg-[#796a61]"
                >
                  <Link to="/">Apri il menu</Link>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  className="h-12 rounded-2xl border-[#d8ccc3] bg-white text-[#5b4d46] hover:bg-[#faf4ef]"
                >
                  <Link to="/menu-completo#bevande">Vai alle bevande</Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
